import TagManager from 'react-gtm-module';
import ReactGA from 'react-ga';
import ReactPixel from 'react-facebook-pixel';
import Environment from './Environment';
import Logger from './Logger';

// The actual trackers
import FBTracking from './FBTracking';
import PinterestTracking from './PinterestTracking';
import Klaviyo from './Klaviyo';
import GTM from './GTM';
import AdWords from './AdWords';

// Create gtag function for AdWords
window.dataLayer = window.dataLayer || [];
function gtag() {
  window.dataLayer.push(arguments); // eslint-disable-line prefer-rest-params
}

// Helper variables
let trackersInitialized = false;

// used for testing purposes
function setTrackersInitialized(initValue) {
  trackersInitialized = initValue;
}

const pageViewQueue = [];

function handleException(e) {
  try {
    Logger.error(e);
  } catch (ravenError) {
    console.log(`Analytics error handler could not record entry: ${e}`); // eslint-disable-line no-console
    console.log(`Analytics error handler also received: ${ravenError}`); // eslint-disable-line no-console
  }
}

function isTrackingEnabled() {
  return Environment.getEnv === 'PRODUCTION';
}

/**
 * Complete an adwords transaction on cart purchase completion
 * @param {*} purchaseEventData
 */
function klavioUpdateUserPoints(purchaseEventData) {
  try {
    Klaviyo.updateUserPoints(purchaseEventData.res.userPoints);
  } catch (e) {
    handleException(e);
  }
}

/**
 * Complete an adwords transaction on cart purchase completion
 * @param {*} purchaseEventData
 * @param {*} cartInfo
 */
function adWordsCompleteTransaction(purchaseEventData, cartInfo) {
  try {
    AdWords.transactionComplete(purchaseEventData, cartInfo);
  } catch (e) {
    handleException(e);
  }
}

/**
 * we are only considering purchases that have $0 values
 * of promotional items to be a lead rather than actual purchase event.
 * @param {boolean} isNoValuePurchaseFlag
 * @param {*} purchaseEventData
 * @param {*} cartInfo
 */
function fbCompleteTransaction(
  isNoValuePurchaseFlag,
  purchaseEventData,
  cartInfo,
) {
  try {
    if (isNoValuePurchaseFlag) {
      FBTracking.addLead('', 'Digital Promo Item Purchase');
    } else {
      FBTracking.FBTransactionComplete(purchaseEventData, cartInfo);
    }
  } catch (e) {
    handleException(e);
  }
}

/**
 * Complete a pintrest transaction on cart purchase completion
 * @param {*} purchaseEventData
 * @param {*} cartInfo
 */
function pintrestCompleteTransaction(purchaseEventData, cartInfo) {
  try {
    PinterestTracking.transactionComplete(purchaseEventData, cartInfo);
  } catch (e) {
    handleException(e);
  }
}

/**
 * Does a quick check of the cart for digital only 0 value items
 * @param {*} cartInfo graphql query {GraphQLTags.queries.cartInfoQuery}
 * @returns {boolean} if the cart has a actual cost on any of the items we
 * consider it to have value
 */
function isNoValuePurchase(cartInfo) {
  try {
    if (
      cartInfo.isCartDigitalOnly &&
      cartInfo.productsAndDetails.reduce((acc, val) => {
        return acc + val.product.actualCost;
      }, 0) === 0
    ) {
      return true;
    }
  } catch (e) {
    handleException(e);
  }
  return false;
}

function getAdvancedMatchingData(data) {
  if (!data) {
    return null;
  }

  const email = data.email || data.guestCheckoutEmail;
  const advancedMatchingData = {
    em: email.toLowerCase(),
    fn: data.name.first.toLowerCase(),
    ln: data.name.last.toLowerCase(),
    ph: data.phoneNumber,
  };

  if (data.billingAddress && data.billingAddress.zipCode) {
    advancedMatchingData.zp = data.billingAddress.zipCode;
    advancedMatchingData.ct = data.billingAddress.city.toLowerCase();
    advancedMatchingData.st = data.billingAddress.state.toLowerCase();
  } else if (data.purchases && data.purchases.length) {
    const paidPurchases = data.purchases.filter(
      (purchase) => purchase.paidStatus === 'PAID',
    );
    const mostRecentPurchase =
      paidPurchases && paidPurchases.length ? paidPurchases.pop() : null;
    if (
      mostRecentPurchase &&
      mostRecentPurchase.order &&
      mostRecentPurchase.order.billingAddress
    ) {
      advancedMatchingData.zp = mostRecentPurchase.order.billingAddress.zipCode;
    }
  }

  return advancedMatchingData;
}

/**
 * Handles events for when a user changes pages even if the
 * page load is handled client side.
 */
const pageViewEvent = async (page, options) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        if (!trackersInitialized) {
          pageViewQueue.push({ page, options });
        } else {
          try {
            FBTracking.pageLoaded();
          } catch (e) {
            handleException(e);
          }

          ReactGA.set({
            page,
            ...options,
          });

          try {
            gtag('config', Environment.getGoogleAdWordsId, { page_path: page }); // eslint-disable-line camelcase
          } catch (e) {
            handleException(e);
          }

          try {
            ReactGA.pageview(page);
          } catch (e) {
            handleException(e);
          }

          try {
            PinterestTracking.pageLoaded(page);
          } catch (e) {
            handleException(e);
          }
        }
      }
      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track page. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const runTheInits = async (data, overrideAlreadyInitializedCheck) => {
  const advancedMatchingData = getAdvancedMatchingData(data);
  return new Promise((resolve, reject) => {
    if (
      isTrackingEnabled() &&
      (!trackersInitialized || overrideAlreadyInitializedCheck)
    ) {
      try {
        TagManager.initialize({
          gtmId: Environment.getGTMId,
        });

        ReactGA.initialize(Environment.getGoogleAnalyticsTrackingId, {
          gaOptions: {
            siteSpeedSampleRate: 50,
            allowLinker: true,
          },
        });

        if (Environment.store === 'bump') {
          ReactGA.plugin.require('linker');
          ReactGA.plugin.execute('linker', 'autoLink', [
            'blog.bumpboxes.com',
            'blog.bitsyboxes.com',
            'bumpboxes.com',
          ]);
        } else if (Environment.store === 'bitsy') {
          ReactGA.plugin.require('linker');
          ReactGA.plugin.execute('linker', 'autoLink', [
            'blog.bumpboxes.com',
            'blog.bitsyboxes.com',
            'bitsyboxes.com',
          ]);
        }

        try {
          ReactGA.plugin.require('ec');
        } catch (e) {
          handleException(e);
        }

        // Now set up the Google Optimize handling
        if (Environment.getGoogleOptimizeId) {
          ReactGA.ga('require', Environment.getGoogleOptimizeId);
        }
        // window.addEventListener('load', runTheInits, { once: true });
      } catch (e) {
        handleException(e);
      }

      try {
        if (advancedMatchingData) {
          ReactPixel.init(Environment.getFacebookPixelId, advancedMatchingData);
        } else {
          ReactPixel.init(Environment.getFacebookPixelId);
        }

        // Must happen before reprocessing the pageViewQueue
        trackersInitialized = true;
        pageViewQueue.forEach((pageEventObject) => {
          pageViewEvent(pageEventObject.page, pageEventObject.options);
        });
        resolve();
      } catch (e) {
        handleException(e);
        reject(`runTheInits broke. ${e}`); // eslint-disable-line prefer-promise-reject-errors
      }
    } else {
      resolve();
    }
  });
};

/**
 * Handles events for brand new users
 */
const listSignupEvent = async (userData) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          Klaviyo.identifyUser({
            email: userData.email,
            source: userData.source,
          });
        } catch (e) {
          handleException(e);
        }
      }

      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track listSignupEvent. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

/**
 * Handles events for brand new users
 */
const newUserEvent = async (userData) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          Klaviyo.addUserProfile(userData);
        } catch (e) {
          handleException(e);
        }
      }

      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track newUserEvent. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

/**
 * Handles events for when an existing user has returned
 */
const returningUserEvent = async (userData, page) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          Klaviyo.addUserProfile(userData);
          GTM.accountDetailsUpdated(page);
        } catch (e) {
          handleException(e);
        }
      }

      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track returningUserEvent. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

/**
 * Handles events for when a purchase/conversion happens
 */
const purchaseEvent = async (purchaseEventData, cartInfo) => {
  return new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        /**
         * we are using this flag to change how analytics are
         * handled for FB and GA. If we only have digital free items
         * (currently only digital prenancy and gender announcements)
         * then it isn't considered a full purchase. FB is treated
         * as a lead and GA is not completed like a normal purchase
         */
        const isNoValuePurchaseFlag = isNoValuePurchase(cartInfo);
        try {
          if (!isNoValuePurchaseFlag) {
            GTM.transactionComplete(purchaseEventData, cartInfo);
            if (
              cartInfo &&
              cartInfo.shippingInsurance &&
              cartInfo.shippingInsurance.cost
            ) {
              GTM.addShippingInsurance();
            }
          }
        } catch (e) {
          handleException(e);
        }

        klavioUpdateUserPoints(purchaseEventData);

        pintrestCompleteTransaction(purchaseEventData, cartInfo);

        /**
         * we are only considering purchases that have $0 values
         * of promotional items to be a lead rather than actual purchase event.
         */
        fbCompleteTransaction(
          isNoValuePurchaseFlag,
          purchaseEventData,
          cartInfo,
        );

        adWordsCompleteTransaction(purchaseEventData, cartInfo);
      }

      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track purchaseEvent. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });
};

/**
 * Handles events for when a user views a single product
 */
const viewedProductEvent = async (productData, isPromo, position) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          Klaviyo.viewedProduct(productData);
        } catch (e) {
          handleException(e);
        }
        try {
          const isProduct = true;
          GTM.viewProduct(productData, isProduct, isPromo, position);
        } catch (e) {
          handleException(e);
        }
      }

      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track viewedProductEvent. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

/**
 * Handles events for when a user views a subscription
 */
const viewedSubscriptionEvent = async (subscriptionData) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          Klaviyo.viewedSubscription(subscriptionData);
        } catch (e) {
          handleException(e);
        }
        try {
          const isProduct = false;
          GTM.viewProduct(subscriptionData, isProduct);
        } catch (e) {
          handleException(e);
        }
      }

      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track viewedSubscriptionEvent. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

/**
 * Handles events for when a user's cart is updated
 */
const updatedCartEvent = async (cartData, itemTrackingObj, itemToAdd, list) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          Klaviyo.updatedCart(cartData);
        } catch (e) {
          handleException(e);
        }

        try {
          PinterestTracking.updatedCart(cartData);
        } catch (e) {
          handleException(e);
        }

        if (itemTrackingObj) {
          const { category, action, item } = itemTrackingObj;
          try {
            ReactGA.event({
              category,
              action,
              label: item,
            });
          } catch (e) {
            handleException(e);
          }
        }

        if (itemToAdd) {
          try {
            GTM.addToCart(cartData, itemToAdd, list);
          } catch (e) {
            handleException(e);
          }
          try {
            FBTracking.addToCart(cartData);
          } catch (e) {
            handleException(e);
          }
        } else {
          try {
            GTM.addToCart(cartData);
          } catch (e) {
            handleException(e);
          }
        }
      }

      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track updatedCartEvent. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

/**
 * Handles events for when a user change their due date
 */
const setDueDateEvent = async (date, email) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          Klaviyo.setDueDateForUser(date, email);
        } catch (e) {
          handleException(e);
        }
      }

      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track setDueDateEvent. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const itemsLoaded = async (event) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        gtag('event', event);
      }
      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track items loaded. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

/**
 * Handles events for starting checkout
 */
const checkoutStarted = async (cart) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          Klaviyo.checkoutStarted(cart);
        } catch (e) {
          handleException(e);
        }
        try {
          FBTracking.checkoutStarted(cart);
        } catch (e) {
          handleException(e);
        }
      }
      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track checkoutStarted. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const checkoutProcess = async (cartInfo, stepNum) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          GTM.checkoutProcess(cartInfo, stepNum);
        } catch (e) {
          handleException(e);
        }
      }
      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track checkout navigation step ${stepNum}. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const breastPumpProcess = async (stepNum, email, bpData, callFbTracking) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          GTM.breastPumpProcess(stepNum);
          Klaviyo.breastPumpProcess(stepNum, email, bpData);
          if (stepNum === 3 && callFbTracking) {
            FBTracking.addLead(bpData, 'Breast Pump');
          }
        } catch (e) {
          handleException(e);
        }
      }
      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track breastpump navigation step ${stepNum}. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const viewOrClickPromo = async (promoData, isViewOrClick) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          GTM.viewOrClickPromo(promoData, isViewOrClick);
        } catch (e) {
          handleException(e);
        }
      }

      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track promo action. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const updatedCreditCard = async (hasError) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          GTM.clickUpdateCreditCard(hasError);
        } catch (e) {
          handleException(e);
        }
      }

      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track credit card update action. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const myAccountDropDown = async (dropdownItem) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          GTM.clickMyAccountDropdown(dropdownItem);
        } catch (e) {
          handleException(e);
        }
      }

      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track My Account dropdown action. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const subscriptionAddOnsChanged = async (action, label) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          ReactGA.event({
            category: 'MyAccount',
            action,
            label,
          });
        } catch (e) {
          handleException(e);
        }
      }
      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track subscriptionAddOnsChanged event. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const viewPartner = async (email, partner) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          Klaviyo.viewPartner(email, partner);
        } catch (e) {
          handleException(e);
        }
        try {
          GTM.clickInsurancePartnerLink(partner);
        } catch (e) {
          handleException(e);
        }
      }
      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track viewPartner. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const createRegistry = async (email, dueDate) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          Klaviyo.addRegistryToUser(email, dueDate);
        } catch (e) {
          handleException(e);
        }
      }
      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track createRegistry. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const breastPumpPartnerApiInfoSent = async (email, partner) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          Klaviyo.breastPumpPartnerApiInfoSent(email, partner);
        } catch (e) {
          handleException(e);
        }
        try {
          GTM.breastPumpPartnerApiInfoSent(partner);
        } catch (e) {
          handleException(e);
        }
      }
      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track viewPartner. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const registryProcess = async (stepNum, email, registryCode) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          GTM.registryProcess(stepNum);
          Klaviyo.registryProcess(stepNum, email, registryCode);
        } catch (e) {
          handleException(e);
        }
      }
      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track breastpump navigation step ${stepNum}. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const addActivationEvent = (eventNameString) => {
  gtag('event', eventNameString);
};

const clickedTrackingNumber = async () =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          GTM.clickedTrackingNumber();
        } catch (e) {
          handleException(e);
        }
      }

      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track Clicked Tracking Number action. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const corpGiftingSubmit = async () =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          GTM.corpGiftingSubmit();
        } catch (e) {
          handleException(e);
        }
      }

      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track Corporate Gifting Submit action. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const dueDateCalcSubmitted = async (isOptedIn) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          GTM.dueDateCalcSubmitted();
          if (isOptedIn) {
            GTM.dueDateCalcOptedIntoEmail();
          }
        } catch (e) {
          handleException(e);
        }
      }
      resolve();
    } catch (e) {
      handleException(e);
      reject(`dueDateCalcSubmitted, ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const scheduledCancellationOrCompletion = async () =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          GTM.scheduledCancel();
        } catch (e) {
          handleException(e);
        }
      }
      resolve();
    } catch (e) {
      handleException(e);
      reject(`scheduledCancellationOrCompletion, ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const extendedSubscription = async () =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          GTM.extendedSubscription();
        } catch (e) {
          handleException(e);
        }
      }
      resolve();
    } catch (e) {
      handleException(e);
      reject(`extendedSubscription, ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const editPaymentUsed = async () =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          GTM.editPaymentUsed();
        } catch (e) {
          handleException(e);
        }
      }
      resolve();
    } catch (e) {
      handleException(e);
      reject(`editPaymentUsed, ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const giveawayEntry = async () =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          GTM.giveawayEntry();
          FBTracking.addLead('', 'Giveaway Entry');
        } catch (e) {
          handleException(e);
        }
      }

      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track Giveaway Entry action. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const lifeInsuranceEntry = async (state) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          GTM.lifeInsuranceEntry(state);
        } catch (e) {
          handleException(e);
        }
      }

      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track Life Insurance Entry action. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

const referralButtonClicked = async (buttonClicked) =>
  new Promise((resolve, reject) => {
    try {
      if (isTrackingEnabled()) {
        try {
          GTM.clickedReferralButton(buttonClicked);
        } catch (e) {
          handleException(e);
        }
      }

      resolve();
    } catch (e) {
      handleException(e);
      reject(`Could not track Refer-A-Friend button clicked action. ${e}`); // eslint-disable-line prefer-promise-reject-errors
    }
  });

export default {
  setTrackersInitialized, // exported for testing purposes
  pageViewEvent,
  listSignupEvent,
  newUserEvent,
  returningUserEvent,
  purchaseEvent,
  viewedProductEvent,
  viewedSubscriptionEvent,
  updatedCartEvent,
  setDueDateEvent,
  itemsLoaded,
  checkoutStarted,
  checkoutProcess,
  breastPumpProcess,
  runTheInits,
  viewOrClickPromo,
  updatedCreditCard,
  subscriptionAddOnsChanged,
  myAccountDropDown,
  viewPartner,
  createRegistry,
  breastPumpPartnerApiInfoSent,
  registryProcess,
  addActivationEvent,
  clickedTrackingNumber,
  corpGiftingSubmit,
  dueDateCalcSubmitted,
  scheduledCancellationOrCompletion,
  extendedSubscription,
  editPaymentUsed,
  giveawayEntry,
  lifeInsuranceEntry,
  referralButtonClicked,
};
